import type { Post } from '@mg/shared';

/**
 * What a post's release timing actually is, as one decision.
 *
 * The BRANCHING is shared; the wording is not. Three surfaces report this — the composer's status
 * line, the campaign view's PostCard, and PostTile — and each phrases it differently ("Scheduled for
 * …", "Queued · …"). Only the ordering has to agree, and it did not: the composer learned about
 * `publishWhenApproved` and the two cards did not, so a post set to "Post as soon as approved" kept
 * reporting "Not scheduled" on its campaign — the flag was saved, it simply was not read.
 *
 * Order matters and is the whole point:
 *  - `publishedAt` wins outright; a published post's former schedule is history.
 *  - a fixed `scheduledAt` beats the flag, because the two are mutually exclusive and a stale flag
 *    alongside a real time should not hide the time.
 *  - `publishWhenApproved` IS a release choice, just not a dated one.
 *  - a dropped slot (`rescheduleRequiredFrom`) is not the same as never having chosen — reporting it
 *    as "Not scheduled" reads as if the user never picked a time.
 */
export type PostScheduleState =
  | { kind: 'published'; at: string }
  | { kind: 'scheduled'; at: string; queued: boolean }
  | { kind: 'on-approval' }
  | { kind: 'reschedule-required'; droppedFrom: string }
  | { kind: 'none' };

export function postScheduleState(post: Post): PostScheduleState {
  if (post.publishedAt) return { kind: 'published', at: post.publishedAt };
  if (post.scheduledAt) {
    return { kind: 'scheduled', at: post.scheduledAt, queued: Boolean(post.queued) };
  }
  if (post.publishWhenApproved) return { kind: 'on-approval' };
  if (post.rescheduleRequiredFrom) {
    return { kind: 'reschedule-required', droppedFrom: post.rescheduleRequiredFrom };
  }
  return { kind: 'none' };
}

/** The wording every surface uses for the two undated states, so those at least stay identical. */
export const ON_APPROVAL_LABEL = 'Post as soon as approved';
export const RESCHEDULE_LABEL = 'Reschedule required';
export const UNSCHEDULED_LABEL = 'Not scheduled';
